import { useCallback } from "react";
import { message } from "antd";
import { ROUTES } from "../Routes";
import { openUrlInTab } from "../library/helpers";

// Appends a params object to a url as query string. Callers pass the real URL
// query keys directly (e.g. { loc, message_id }); each key/value is appended
// verbatim. Skips empty/null/NaN values and picks the right ?/& separator.
const appendParams = (url, params = {}) => {
  const pairs = Object.entries(params)
    .filter(([, value]) => {
      if (value === null || value === undefined || value === "") return false;
      if (typeof value === "number" && Number.isNaN(value)) return false;
      return true;
    })
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  if (pairs.length === 0) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${pairs.join("&")}`;
};

export const REDIRECTION_TYPES = {
  PINNED: "PINNED",
  BY_LEAD_ID: "BY_LEAD_ID",
  BY_LEAD_PHONE: "BY_LEAD_PHONE",
};

const DEFAULT_REDIRECTION_TYPE = REDIRECTION_TYPES.BY_LEAD_ID;

const useLeadRedirection = ({
  redirectionType = DEFAULT_REDIRECTION_TYPE,
  basePath = ROUTES.activityCenter,
} = {}) => {
  const redirectByPinned = useCallback(
    (leadId, params = {}) => {
      if (!leadId) {
        message.error("Lead ID is required for redirection");
        return;
      }
      localStorage.setItem("clickLeadId", leadId);
      window.open(appendParams(basePath, params), "_blank");
    },
    [basePath]
  );

  const redirectByLeadId = useCallback(
    (leadId, params = {}) => {
      if (!leadId) {
        message.error("Lead ID is required for redirection");
        return;
      }
      const url = appendParams(`${basePath}?leadId=${leadId}`, params);
      openUrlInTab(url, "_blank", true);
    },
    [basePath]
  );

  const redirectByPhone = useCallback(
    (phone, params = {}) => {
      if (!phone) {
        message.error("Phone number is required for redirection");
        return;
      }
      const url = appendParams(`${basePath}/${phone}`, params);
      openUrlInTab(url, "_blank", true);
    },
    [basePath]
  );

  const redirectToLead = useCallback(
    (...args) => {
      switch (redirectionType) {
        case REDIRECTION_TYPES.BY_LEAD_ID:
          return redirectByLeadId(...args);
        case REDIRECTION_TYPES.BY_LEAD_PHONE:
          return redirectByPhone(...args);
        case REDIRECTION_TYPES.PINNED:
        default:
          return redirectByPinned(...args);
      }
    },
    [redirectionType, redirectByPinned, redirectByLeadId, redirectByPhone]
  );

  return { redirectToLead, redirectByPinned, redirectByLeadId, redirectByPhone };
};

export default useLeadRedirection;
